// AppConfigProvider — fetches /api/config once on mount and exposes the
// resulting flags via context. While loading (and on error), we fall back
// to the conservative defaults (banner visible, debug panel hidden).
//
// Placed above SessionProvider in App.tsx: this endpoint takes no role, so
// mounting it early lets MockedDepsBanner render even on /signin without
// flicker after session load.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { DEFAULT_APP_CONFIG, getAppConfig, type AppConfig } from "@/api";
import { AppConfigContext, type AppConfigContextValue } from "./context";

export function AppConfigProvider({ children }: { children: ReactNode }): JSX.Element {
  const [config, setConfig] = useState<AppConfig>({ ...DEFAULT_APP_CONFIG });
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const next = await getAppConfig();
        if (!cancelled) setConfig(next);
      } catch (err) {
        // Mounted above ToastProvider so we cannot route through the api
        // error dispatcher. Conservative defaults (banner visible, debug
        // panel hidden) already applied above — log and move on.
        // eslint-disable-next-line no-console
        console.warn("[config] failed to load /api/config; using defaults", err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AppConfigContextValue>(
    () => ({ ...config, loaded }),
    [config, loaded],
  );

  return (
    <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
  );
}
